import { useState, useEffect, useMemo, useRef, forwardRef } from 'react';
import { axiosInstance } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Plus, Trash2, Edit, Eye, Send, Download, Search, X, ShieldCheck, FileText } from 'lucide-react';
import { toast } from 'sonner';
import html2pdf from 'html2pdf.js';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_dreamy-mcnulty-2/artifacts/39goexey_JPEG-afbeelding-4738-BFE8-D0-0.jpeg';

const DEFAULT_EXCLUSIONS = [
  'onjuist gebruik',
  'achterstallig onderhoud',
  'schade door derden',
  'extreme weersomstandigheden of overmacht',
];

const emptyForm = () => ({
  client_name: '',
  client_address: '',
  client_city: '',
  client_email: '',
  client_phone: '',
  invoice_id: '',
  invoice_reference: '',
  issue_date: new Date().toLocaleDateString('nl-NL'),
  period_text: '5 jaar',
  work_description: 'alle door ons geplaatste onderdelen bestaande uit hardhout en geïmpregneerd hout',
  materials: '',
  exclusions: [...DEFAULT_EXCLUSIONS],
  notes: '',
  signature_name: 'Jhoniffer Cardoso',
});

const Warranties = ({ user }) => {
  const [warranties, setWarranties] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [previewing, setPreviewing] = useState(null);
  const [search, setSearch] = useState('');
  const previewRef = useRef(null);

  useEffect(() => {
    if (!user?.is_admin) return;
    fetchAll();
  }, [user]);

  const fetchAll = async () => {
    try {
      const [w, i] = await Promise.all([
        axiosInstance.get('/warranties'),
        axiosInstance.get('/invoices'),
      ]);
      setWarranties(w.data || []);
      setInvoices(i.data || []);
    } catch (e) {
      toast.error('Erro ao carregar garantias');
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setOpen(true);
  };

  const openEdit = (w) => {
    setEditing(w);
    setForm({
      client_name: w.client_name || '',
      client_address: w.client_address || '',
      client_city: w.client_city || '',
      client_email: w.client_email || '',
      client_phone: w.client_phone || '',
      invoice_id: w.invoice_id || '',
      invoice_reference: w.invoice_reference || '',
      issue_date: w.issue_date || '',
      period_text: w.period_text || '5 jaar',
      work_description: w.work_description || '',
      materials: w.materials || '',
      exclusions: w.exclusions && w.exclusions.length ? [...w.exclusions] : [...DEFAULT_EXCLUSIONS],
      notes: w.notes || '',
      signature_name: w.signature_name || 'Jhoniffer Cardoso',
    });
    setOpen(true);
  };

  const handlePickInvoice = (invId) => {
    const inv = invoices.find((i) => i.id === invId);
    if (!inv) {
      setForm((f) => ({ ...f, invoice_id: '', invoice_reference: '' }));
      return;
    }
    setForm((f) => ({
      ...f,
      invoice_id: inv.id,
      invoice_reference: inv.number || '',
      client_name: f.client_name || inv.client_name || '',
      client_address: f.client_address || inv.client_address || '',
      client_city: f.client_city || inv.client_city || '',
      client_email: f.client_email || inv.client_email || '',
      client_phone: f.client_phone || inv.client_phone || '',
      issue_date: f.issue_date || inv.invoice_date || f.issue_date,
    }));
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    if (!form.client_name.trim()) {
      toast.error('Naam klant is verplicht');
      return;
    }
    try {
      const payload = { ...form, exclusions: form.exclusions.filter((x) => (x || '').trim()) };
      if (editing) {
        await axiosInstance.put(`/warranties/${editing.id}`, payload);
        toast.success('Garantie bijgewerkt');
      } else {
        await axiosInstance.post('/warranties', payload);
        toast.success('Garantie aangemaakt');
      }
      setOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Opslaan mislukt');
    }
  };

  const handleDelete = async (w) => {
    if (!window.confirm(`Garantie ${w.number} echt verwijderen?`)) return;
    try {
      await axiosInstance.delete(`/warranties/${w.id}`);
      toast.success('Verwijderd');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verwijderen mislukt');
    }
  };

  const handleSendEmail = async (w) => {
    if (!w.client_email) {
      toast.error('Klant heeft geen e-mailadres — voeg eerst een e-mail toe.');
      return;
    }
    if (!window.confirm(`Garantie ${w.number} per e-mail sturen naar ${w.client_email}?`)) return;
    try {
      const r = await axiosInstance.post(`/warranties/${w.id}/send-email`);
      toast.success(`Verzonden naar ${r.data.email}`);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Versturen mislukt');
    }
  };

  const handleDownloadPdf = async (w) => {
    setPreviewing(w);
    // wait for the hidden printable to mount
    await new Promise((r) => setTimeout(r, 200));
    if (!previewRef.current) {
      toast.error('Voorbeeld niet gereed — probeer opnieuw');
      return;
    }
    const opt = {
      margin: 10,
      filename: `Garantie-${w.number}.pdf`,
      image: { type: 'jpeg', quality: 0.96 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    };
    try {
      await html2pdf().set(opt).from(previewRef.current).save();
    } finally {
      setPreviewing(null);
    }
  };

  const updateExclusion = (idx, val) => {
    setForm((f) => {
      const list = [...f.exclusions];
      list[idx] = val;
      return { ...f, exclusions: list };
    });
  };
  const removeExclusion = (idx) => {
    setForm((f) => ({ ...f, exclusions: f.exclusions.filter((_, i) => i !== idx) }));
  };
  const addExclusion = () => setForm((f) => ({ ...f, exclusions: [...f.exclusions, ''] }));

  const sLower = search.trim().toLowerCase();
  const filtered = useMemo(() => {
    if (!sLower) return warranties;
    return warranties.filter((w) =>
      [w.number, w.client_name, w.client_address, w.client_email, w.invoice_reference]
        .filter(Boolean)
        .some((x) => x.toLowerCase().includes(sLower))
    );
  }, [warranties, sLower]);

  if (!user?.is_admin) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-800 text-lg">Acesso restrito a administradores</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold text-gray-800 flex items-center gap-2">
            <ShieldCheck className="w-8 h-8 text-[#3da5a5]" /> Garanties
            <span className="text-base text-gray-400 font-normal ml-2" data-testid="warranties-counter">
              ({warranties.length})
            </span>
          </h1>
          <p className="text-sm text-gray-500 mt-1">Geef professionele garantiebewijzen aan uw klanten — per project aanpasbaar.</p>
        </div>
        <Button onClick={openCreate} className="bg-[#3da5a5] hover:bg-[#2d8585]" data-testid="new-warranty-btn">
          <Plus className="w-4 h-4 mr-2" /> Nieuwe Garantie
        </Button>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        <Input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Zoek op nummer, klant, adres, factuur..."
          className="pl-9 pr-9 bg-white border-gray-200"
          data-testid="warranties-search"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch('')}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((w) => (
          <Card key={w.id} className="bg-white shadow-sm border-gray-200" data-testid={`warranty-card-${w.id}`}>
            <CardContent className="p-5 space-y-3">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs text-gray-500">Garantiebewijs</p>
                  <p className="text-lg font-bold text-[#1a5276]">{w.number}</p>
                </div>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${w.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                  {w.status === 'sent' ? 'Verzonden' : 'Concept'}
                </span>
              </div>
              <div className="text-sm space-y-0.5">
                <p className="font-semibold text-gray-800">{w.client_name}</p>
                <p className="text-gray-500 text-xs">{w.client_address} {w.client_city && `· ${w.client_city}`}</p>
                {w.client_email && <p className="text-gray-400 text-[11px]">{w.client_email}</p>}
              </div>
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="px-2 py-0.5 bg-[#3da5a5]/10 text-[#1a5276] rounded">⏱ {w.period_text}</span>
                {w.invoice_reference && <span className="px-2 py-0.5 bg-purple-50 text-purple-700 rounded">📄 {w.invoice_reference}</span>}
                <span className="px-2 py-0.5 bg-gray-50 text-gray-600 rounded">📅 {w.issue_date}</span>
              </div>
              <div className="flex gap-1 pt-2 border-t border-gray-100">
                <Button size="sm" variant="outline" onClick={() => handleDownloadPdf(w)} className="flex-1 text-[#1a5276] border-[#1a5276]/30" data-testid={`download-pdf-${w.id}`}>
                  <Download className="w-3 h-3 mr-1" /> PDF
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleSendEmail(w)} className="text-amber-600 border-amber-300" disabled={!w.client_email} title={w.client_email ? '' : 'Geen e-mail'} data-testid={`send-email-${w.id}`}>
                  <Send className="w-3 h-3" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => window.open(`/garantie/${w.public_token}`, '_blank')} className="text-[#3da5a5] border-[#3da5a5]/30" data-testid={`view-warranty-${w.id}`}>
                  <Eye className="w-3 h-3" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => openEdit(w)} className="text-blue-500 border-blue-300" data-testid={`edit-warranty-${w.id}`}>
                  <Edit className="w-3 h-3" />
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleDelete(w)} className="text-red-500 border-red-300" data-testid={`delete-warranty-${w.id}`}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {filtered.length === 0 && (
          <p className="col-span-full text-center text-gray-500 italic py-12">
            {search ? 'Geen resultaten' : 'Nog geen garanties. Maak uw eerste garantiebewijs aan.'}
          </p>
        )}
      </div>

      {/* Dialog: Create / Edit */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white text-gray-800 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-[#3da5a5]" />
              {editing ? `Bewerken: ${editing.number}` : 'Nieuwe Garantie'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSave} className="space-y-4">
            {/* Optional: link to invoice */}
            <div>
              <Label className="text-sm">Factuur koppelen (optioneel)</Label>
              <Select value={form.invoice_id || 'none'} onValueChange={(v) => handlePickInvoice(v === 'none' ? '' : v)}>
                <SelectTrigger className="bg-gray-50" data-testid="warranty-invoice-select">
                  <SelectValue placeholder="Geen factuur" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Geen factuur (handmatige referentie) —</SelectItem>
                  {invoices.map((inv) => (
                    <SelectItem key={inv.id} value={inv.id}>
                      {inv.number} — {inv.client_name || ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input
                value={form.invoice_reference}
                onChange={(e) => setForm({ ...form, invoice_reference: e.target.value })}
                placeholder="Of typ vrij: bijv. 2026-00026"
                className="mt-2 bg-gray-50"
                data-testid="warranty-invoice-reference"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Naam klant *</Label>
                <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} required className="bg-gray-50" data-testid="warranty-client-name" />
              </div>
              <div>
                <Label className="text-sm">E-mail</Label>
                <Input type="email" value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} className="bg-gray-50" data-testid="warranty-client-email" />
              </div>
              <div>
                <Label className="text-sm">Adres</Label>
                <Input value={form.client_address} onChange={(e) => setForm({ ...form, client_address: e.target.value })} className="bg-gray-50" />
              </div>
              <div>
                <Label className="text-sm">Plaats</Label>
                <Input value={form.client_city} onChange={(e) => setForm({ ...form, client_city: e.target.value })} className="bg-gray-50" />
              </div>
              <div>
                <Label className="text-sm">Telefoon</Label>
                <Input value={form.client_phone} onChange={(e) => setForm({ ...form, client_phone: e.target.value })} className="bg-gray-50" />
              </div>
              <div>
                <Label className="text-sm">Opleverdatum / Factuurdatum</Label>
                <Input value={form.issue_date} onChange={(e) => setForm({ ...form, issue_date: e.target.value })} placeholder="22-05-2026" className="bg-gray-50" data-testid="warranty-issue-date" />
              </div>
            </div>

            <div>
              <Label className="text-sm">Garantieperiode <span className="text-gray-400">(vrije tekst, bv. "5 jaar", "18 maanden", "10 jaar")</span></Label>
              <Input value={form.period_text} onChange={(e) => setForm({ ...form, period_text: e.target.value })} className="bg-gray-50" data-testid="warranty-period" />
            </div>

            <div>
              <Label className="text-sm">Werk- / materiaalomschrijving</Label>
              <Textarea
                rows={3}
                value={form.work_description}
                onChange={(e) => setForm({ ...form, work_description: e.target.value })}
                placeholder="alle door ons geplaatste onderdelen bestaande uit hardhout en geïmpregneerd hout"
                className="bg-gray-50"
                data-testid="warranty-work-description"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-sm">Uitsluitingen <span className="text-gray-400">(garantie vervalt bij...)</span></Label>
                <Button type="button" size="sm" variant="ghost" onClick={addExclusion} className="text-[#3da5a5] h-7" data-testid="warranty-add-exclusion">
                  <Plus className="w-3 h-3 mr-1" /> Regel toevoegen
                </Button>
              </div>
              <div className="space-y-2">
                {form.exclusions.map((ex, idx) => (
                  <div key={idx} className="flex gap-2">
                    <Input
                      value={ex}
                      onChange={(e) => updateExclusion(idx, e.target.value)}
                      className="bg-gray-50"
                      placeholder="bv. onjuist gebruik"
                      data-testid={`warranty-exclusion-${idx}`}
                    />
                    <Button type="button" size="sm" variant="ghost" onClick={() => removeExclusion(idx)} className="text-red-500">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-sm">Extra opmerkingen (optioneel)</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-gray-50" />
            </div>

            <div>
              <Label className="text-sm">Handtekening naam</Label>
              <Input value={form.signature_name} onChange={(e) => setForm({ ...form, signature_name: e.target.value })} className="bg-gray-50" />
            </div>

            <Button type="submit" className="w-full bg-[#3da5a5] hover:bg-[#2d8585]" data-testid="warranty-save-btn">
              {editing ? 'Bijwerken' : 'Garantiebewijs aanmaken'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Hidden printable preview used by html2pdf */}
      {previewing && (
        <div style={{ position: 'fixed', left: '-9999px', top: 0, width: '794px' }}>
          <PrintableWarranty warranty={previewing} ref={previewRef} />
        </div>
      )}
    </div>
  );
};

// Printable warranty document — also used by /garantie/{token} public viewer.
// eslint-disable-next-line react/display-name
export const PrintableWarranty = forwardRef(({ warranty }, ref) => {
  return (
    <div ref={ref} style={{ background: '#ffffff', color: '#111827', padding: '40px', fontFamily: 'Arial, sans-serif', minHeight: '1000px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '3px solid #1a5276', paddingBottom: '20px', marginBottom: '30px' }}>
        <div>
          <img src={LOGO_URL} alt="Cardoso Bouw" style={{ height: '70px', marginBottom: '10px' }} crossOrigin="anonymous" />
          <p style={{ margin: 0, fontSize: '13px', color: '#374151' }}>Cardoso Bouw en Onderhoud</p>
          <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>Vuntuslaan 69, 1231 NR Loosdrecht</p>
          <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>info@cardosobouw.nl · 06 8507 1009</p>
          <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>KvK 85310646 · BTW NL004077138B58</p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <h1 style={{ fontSize: '28px', color: '#1a5276', margin: 0, letterSpacing: '0.05em', fontWeight: 800 }}>GARANTIEBEWIJS</h1>
          <p style={{ fontSize: '20px', fontWeight: 700, color: '#3da5a5', margin: '8px 0 0 0' }}>{warranty.number}</p>
          <p style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0 0 0' }}>Datum: {warranty.issue_date}</p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '30px' }}>
        <div>
          <p style={{ fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0 }}>Verstrekt aan</p>
          <p style={{ fontSize: '16px', fontWeight: 700, color: '#111827', margin: '4px 0' }}>{warranty.client_name}</p>
          <p style={{ fontSize: '13px', color: '#374151', margin: 0 }}>{warranty.client_address}</p>
          {warranty.client_city && <p style={{ fontSize: '13px', color: '#374151', margin: 0 }}>{warranty.client_city}</p>}
          {warranty.client_email && <p style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0 0 0' }}>{warranty.client_email}</p>}
          {warranty.client_phone && <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>{warranty.client_phone}</p>}
        </div>
        {warranty.invoice_reference && (
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontSize: '11px', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.1em', margin: 0 }}>Factuurreferentie</p>
            <p style={{ fontSize: '16px', fontWeight: 700, color: '#1a5276', margin: '4px 0' }}>{warranty.invoice_reference}</p>
          </div>
        )}
      </div>

      <div style={{ background: '#f0f9f9', borderLeft: '4px solid #3da5a5', padding: '16px 20px', marginBottom: '24px' }}>
        <p style={{ fontSize: '13px', color: '#1a5276', margin: 0, lineHeight: 1.6 }}>
          Hierbij geven wij, <strong>Cardoso Bouw en Onderhoud</strong>, garantie op
          {warranty.work_description ? ` ${warranty.work_description}` : ' alle door ons uitgevoerde werkzaamheden en geplaatste materialen'}.
        </p>
      </div>

      <h2 style={{ fontSize: '16px', color: '#1a5276', borderBottom: '2px solid #e5e7eb', paddingBottom: '6px', marginBottom: '12px' }}>
        Garantievoorwaarden
      </h2>
      <p style={{ fontSize: '13px', color: '#111827', lineHeight: 1.7, marginBottom: '12px' }}>
        De garantieperiode bedraagt <strong style={{ color: '#3da5a5' }}>{warranty.period_text}</strong> vanaf de opleverdatum / factuurdatum ({warranty.issue_date}).
      </p>
      <p style={{ fontSize: '13px', color: '#111827', lineHeight: 1.7, marginBottom: '20px' }}>
        Deze garantie geldt op <strong>constructie en materiaal</strong>, mits de werkzaamheden / materialen op normale wijze worden gebruikt en onderhouden.
      </p>

      <h2 style={{ fontSize: '16px', color: '#1a5276', borderBottom: '2px solid #e5e7eb', paddingBottom: '6px', marginBottom: '12px' }}>
        De garantie vervalt bij:
      </h2>
      <ul style={{ fontSize: '13px', color: '#111827', lineHeight: 1.8, paddingLeft: '22px', marginBottom: '24px' }}>
        {(warranty.exclusions || []).map((ex, i) => (
          <li key={i}>{ex};</li>
        ))}
      </ul>

      {warranty.notes && (
        <div style={{ background: '#fffbeb', borderLeft: '4px solid #f59e0b', padding: '12px 16px', marginBottom: '24px' }}>
          <p style={{ fontSize: '12px', color: '#92400e', margin: 0, whiteSpace: 'pre-wrap' }}>{warranty.notes}</p>
        </div>
      )}

      <div style={{ marginTop: '60px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '40px' }}>
        <div>
          <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 30px 0' }}>Cardoso Bouw en Onderhoud</p>
          <div style={{ borderTop: '1px solid #9ca3af', paddingTop: '6px' }}>
            <p style={{ fontSize: '13px', fontWeight: 600, color: '#111827', margin: 0 }}>{warranty.signature_name}</p>
            <p style={{ fontSize: '11px', color: '#6b7280', margin: '2px 0 0 0' }}>Eigenaar</p>
          </div>
        </div>
        <div style={{ textAlign: 'center', fontSize: '11px', color: '#9ca3af' }}>
          <p style={{ margin: 0 }}>Garantienummer</p>
          <p style={{ fontSize: '22px', fontWeight: 800, color: '#1a5276', letterSpacing: '0.08em', margin: '4px 0' }}>{warranty.number}</p>
          <p style={{ margin: 0 }}>Bewaar dit document zorgvuldig</p>
        </div>
      </div>
    </div>
  );
});

export default Warranties;
